/**
 * Seedance Test Page - Secret test page for Seedance 2.0 API evaluation
 * 
 * This page mirrors the ExtremeActionPanel preview functionality but uses
 * Seedance 2.0 instead of Kling for video generation.
 * 
 * Access via: /seedance-test (secret, unlisted route)
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Zap, Loader2, Image as ImageIcon, Film,
  Upload, Trash2, Plus,
  Sparkles, Check, X, AlertTriangle, Settings2,
  Download, Video, ArrowLeft, Eye, EyeOff, Copy, Info
} from "lucide-react";
import api from "@/config/axiosInstance";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAX_IMAGES = 9;

/**
 * Get proper URL for assets (handles S3 proxying)
 */
const getAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("/api/")) {
    return `${API}${url}`;
  }
  return url;
};

export default function SeedanceTest() {
  const navigate = useNavigate();
  
  // Configuration state
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState("1080p");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [seed, setSeed] = useState(-1);
  const [cameraFixed, setCameraFixed] = useState(false);
  const [audioGeneration, setAudioGeneration] = useState(false);
  const [negativePrompt, setNegativePrompt] = useState("blur, distortion, morphing, low quality, face change");
  
  // Multi-image state - array of {url, label, uploading}
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  
  // Results history
  const [jobHistory, setJobHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  
  // Polling ref
  const pollRef = useRef(null);

  // Load configuration on mount
  useEffect(() => {
    loadConfig();
    loadJobHistory();
    
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get("/seedance-test/config");
      setConfig(response.data);
    } catch (error) {
      console.error("Failed to load Seedance config:", error);
      toast.error("Failed to load Seedance configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadJobHistory = async () => {
    try {
      const response = await api.get("/seedance-test/jobs?limit=10");
      setJobHistory(response.data.jobs || []);
    } catch (error) {
      console.error("Failed to load job history:", error);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const remainingSlots = MAX_IMAGES - images.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    
    if (filesToUpload.length < files.length) {
      toast.warning(`Only ${remainingSlots} slot(s) available. Some images were skipped.`);
    }
    
    for (const file of filesToUpload) {
      const tempId = Date.now() + Math.random();
      const imageIndex = images.length + 1;
      
      // Add placeholder with uploading state
      setImages(prev => [...prev, {
        id: tempId,
        url: null,
        label: `Image ${prev.length + 1}`,
        uploading: true
      }]);
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await api.post("/uploads/temp", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        if (response.data.url) {
          setImages(prev => prev.map(img => 
            img.id === tempId 
              ? { ...img, url: response.data.url, uploading: false }
              : img
          ));
        }
      } catch (error) {
        console.error("Upload error:", error);
        // Use local URL as fallback
        const localUrl = URL.createObjectURL(file);
        setImages(prev => prev.map(img => 
          img.id === tempId 
            ? { ...img, url: localUrl, uploading: false }
            : img
        ));
        toast.warning("Using local preview (upload may have failed)");
      }
    }
  };

  const removeImage = (index) => {
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Re-label remaining images
      return newImages.map((img, i) => ({
        ...img,
        label: `Image ${i + 1}`
      }));
    });
  };

  const updateImageLabel = (index, newLabel) => {
    setImages(prev => prev.map((img, i) => 
      i === index ? { ...img, label: newLabel } : img
    ));
  };

  const copyAtSyntax = (index) => {
    const syntax = `@Image${index + 1}`;
    navigator.clipboard.writeText(syntax);
    toast.success(`Copied ${syntax} to clipboard`);
  };

  const insertAtSyntax = (index) => {
    const syntax = `@Image${index + 1}`;
    setPrompt(prev => prev + (prev.endsWith(" ") || prev === "" ? "" : " ") + syntax + " ");
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }
    
    if (images.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }
    
    const uploadedImages = images.filter(img => img.url && !img.uploading);
    if (uploadedImages.length === 0) {
      toast.error("Please wait for images to finish uploading");
      return;
    }
    
    setGenerating(true);
    setJobStatus(null);
    
    try {
      const response = await api.post("/seedance-test/generate", {
        prompt: prompt.trim(),
        images: uploadedImages.map((img, index) => ({
          url: img.url,
          label: img.label,
          reference: `@Image${index + 1}`
        })),
        duration,
        resolution,
        aspect_ratio: aspectRatio,
        seed,
        camera_fixed: cameraFixed,
        audio_generation: audioGeneration,
        negative_prompt: negativePrompt || null
      });
      
      if (response.data.success) {
        setCurrentJobId(response.data.job_id);
        toast.success("Video generation started!");
        startPolling(response.data.job_id);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to start generation");
      setGenerating(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }
    
    const poll = async () => {
      try {
        const response = await api.get(`/seedance-test/status/${jobId}`);
        setJobStatus(response.data);
        
        if (response.data.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.success("Video generation complete!");
          loadJobHistory();
        } else if (response.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          toast.error(response.data.error_message || "Generation failed");
          loadJobHistory();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await api.delete(`/seedance-test/jobs/${jobId}`);
      setJobHistory(prev => prev.filter(j => j.job_id !== jobId));
      toast.success("Job deleted");
    } catch (error) {
      toast.error("Failed to delete job");
    }
  };

  const handleDownloadVideo = async (videoUrl, jobId) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `seedance_${jobId.slice(0, 8)}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download video");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <h1 className="text-lg font-semibold">Seedance 2.0 Test Lab</h1>
                <Badge variant="outline" className="text-purple-400 border-purple-400/50 text-xs">
                  Secret
                </Badge>
              </div>
            </div>
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
              Powered by Kie.ai
            </Badge>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Generation Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info Banner */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Video className="w-5 h-5 text-purple-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-zinc-300 space-y-1">
                    <p className="font-medium text-purple-300">Testing Seedance 2.0 vs Kling</p>
                    <p className="text-zinc-400 text-xs">
                      Upload up to 9 images and reference them in your prompt using @Image1, @Image2, etc.
                      Best practice: 1-2 images for character consistency.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Multi-Image Upload Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-purple-400" />
                    Reference Images
                    <Badge variant="outline" className="text-xs text-zinc-400">
                      {images.length}/{MAX_IMAGES}
                    </Badge>
                  </CardTitle>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-4 h-4 text-zinc-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Upload images and reference them in your prompt using @Image1, @Image2, etc.</p>
                      <p className="mt-1 text-amber-400">Tip: 1-2 images work best for character consistency.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <CardDescription className="text-xs">
                  Click images to copy @ syntax or use the buttons to insert into prompt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Image Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {images.map((img, index) => (
                    <div key={img.id || index} className="relative group">
                      <div 
                        className="aspect-square rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden cursor-pointer hover:border-purple-500/50 transition-all"
                        onClick={() => copyAtSyntax(index)}
                      >
                        {img.uploading ? (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                          </div>
                        ) : img.url ? (
                          <img 
                            src={getAssetUrl(img.url)} 
                            alt={img.label}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-500">
                            <X className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* @ Syntax Badge */}
                      <div className="absolute -top-2 -left-2 z-10">
                        <Badge 
                          className="bg-purple-600 text-white text-[10px] px-1.5 py-0.5 cursor-pointer hover:bg-purple-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            insertAtSyntax(index);
                          }}
                        >
                          @Image{index + 1}
                        </Badge>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="absolute -top-2 -right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 bg-zinc-800 hover:bg-zinc-700 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyAtSyntax(index);
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 bg-red-900/80 hover:bg-red-800 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      {/* Label */}
                      <Input
                        value={img.label}
                        onChange={(e) => updateImageLabel(index, e.target.value)}
                        className="mt-1 h-6 text-[10px] bg-zinc-800/50 border-zinc-700 text-center px-1"
                        placeholder="Label"
                      />
                    </div>
                  ))}
                  
                  {/* Add Image Button */}
                  {images.length < MAX_IMAGES && (
                    <div 
                      className="aspect-square rounded-lg border border-dashed border-zinc-700 bg-zinc-800/30 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500/50 hover:bg-zinc-800/50 transition-all"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Plus className="w-6 h-6 text-zinc-500 mb-1" />
                      <span className="text-[10px] text-zinc-500">Add Image</span>
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files)}
                />
                
                {/* @ Syntax Help */}
                {images.length > 0 && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                    <p className="text-xs text-zinc-400 mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span className="font-medium">@ Syntax Reference</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {images.map((img, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                          onClick={() => insertAtSyntax(index)}
                        >
                          @Image{index + 1}
                          <span className="ml-1 text-zinc-500 max-w-[60px] truncate">
                            ({img.label})
                          </span>
                        </Button>
                      ))}
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">
                      Click a badge above to insert it into your prompt. Example: "@Image1 walks toward @Image2"
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Prompt */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  Generation Prompt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Textarea
                    placeholder="Describe the motion you want to generate... Use @Image1, @Image2, etc. to reference your uploaded images."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="min-h-[120px] bg-zinc-800/50 border-zinc-700 text-sm"
                    disabled={generating}
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-zinc-500">
                      {prompt.length}/500 characters
                    </p>
                    {images.length > 0 && (
                      <p className="text-[10px] text-purple-400">
                        Available: {images.map((_, i) => `@Image${i + 1}`).join(", ")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Negative Prompt</Label>
                  <Input
                    placeholder="What to avoid in the generation..."
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-sm"
                    disabled={generating}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Generation Settings */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-purple-400" />
                  Generation Settings
                </CardTitle>
                <CardDescription className="text-xs">
                  Configure Seedance 2.0 parameters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Duration */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400">Duration</Label>
                    <span className="text-xs font-medium text-purple-400">{duration}s</span>
                  </div>
                  <Slider
                    value={[duration]}
                    onValueChange={([v]) => setDuration(v)}
                    min={config?.min_duration || 4}
                    max={config?.max_duration || 15}
                    step={1}
                    disabled={generating}
                    className="py-2"
                  />
                  <p className="text-[10px] text-zinc-500">
                    Seedance supports 4-15 seconds
                  </p>
                </div>

                {/* Resolution & Aspect Ratio */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Resolution</Label>
                    <Select value={resolution} onValueChange={setResolution} disabled={generating}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(config?.resolutions || ["480p", "720p", "1080p", "2k"]).map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Aspect Ratio</Label>
                    <Select value={aspectRatio} onValueChange={setAspectRatio} disabled={generating}>
                      <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(config?.aspect_ratios || ["16:9", "9:16", "1:1", "4:3"]).map(ar => (
                          <SelectItem key={ar} value={ar}>{ar}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400">Camera Fixed</Label>
                    <Switch
                      checked={cameraFixed}
                      onCheckedChange={setCameraFixed}
                      disabled={generating}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-zinc-400">Generate Audio</Label>
                    <Switch
                      checked={audioGeneration}
                      onCheckedChange={setAudioGeneration}
                      disabled={generating}
                    />
                  </div>
                </div>

                {/* Seed */}
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-400">Seed (-1 for random)</Label>
                  <Input
                    type="number"
                    value={seed}
                    onChange={(e) => setSeed(parseInt(e.target.value) || -1)}
                    min={-1}
                    max={2147483647}
                    className="bg-zinc-800/50 border-zinc-700 text-sm"
                    disabled={generating}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim() || images.length === 0}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-medium"
              data-testid="generate-seedance-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Generate with Seedance 2.0
                </>
              )}
            </Button>

            {/* Current Job Status */}
            {jobStatus && (
              <Card className={`border ${
                jobStatus.status === "completed" ? "border-green-500/50 bg-green-500/5" :
                jobStatus.status === "failed" ? "border-red-500/50 bg-red-500/5" :
                "border-purple-500/50 bg-purple-500/5"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {jobStatus.status === "completed" ? (
                        <Check className="w-4 h-4 text-green-400" />
                      ) : jobStatus.status === "failed" ? (
                        <X className="w-4 h-4 text-red-400" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      )}
                      <span className="text-sm font-medium capitalize">{jobStatus.status}</span>
                    </div>
                    <span className="text-xs text-zinc-500">
                      {Math.round(jobStatus.progress_percent)}%
                    </span>
                  </div>
                  
                  <Progress value={jobStatus.progress_percent} className="h-2 mb-2" />
                  
                  <p className="text-xs text-zinc-400">{jobStatus.progress_message}</p>
                  
                  {jobStatus.status === "completed" && jobStatus.video_url && (
                    <div className="mt-4">
                      <video
                        src={getAssetUrl(jobStatus.video_url)}
                        controls
                        className="w-full rounded-lg bg-black"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                        onClick={() => handleDownloadVideo(jobStatus.video_url, jobStatus.job_id)}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download Video
                      </Button>
                    </div>
                  )}
                  
                  {jobStatus.status === "failed" && jobStatus.error_message && (
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-400">
                      {jobStatus.error_message}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - History */}
          <div className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Film className="w-4 h-4 text-purple-400" />
                    Generation History
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(!showHistory)}
                    className="h-6 w-6 p-0"
                  >
                    {showHistory ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    {jobHistory.length === 0 ? (
                      <div className="p-4 text-center text-zinc-500 text-sm">
                        No generations yet
                      </div>
                    ) : (
                      <div className="divide-y divide-zinc-800">
                        {jobHistory.map((job) => (
                          <div key={job.job_id} className="p-3 hover:bg-zinc-800/30 transition-colors">
                            <div className="flex items-start gap-2 mb-2">
                              <Badge 
                                variant="outline" 
                                className={`text-[10px] shrink-0 ${
                                  job.status === "completed" ? "text-green-400 border-green-500/50" :
                                  job.status === "failed" ? "text-red-400 border-red-500/50" :
                                  "text-purple-400 border-purple-500/50"
                                }`}
                              >
                                {job.status}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                {new Date(job.created_at).toLocaleString()}
                              </span>
                            </div>
                            
                            <p className="text-xs text-zinc-300 line-clamp-2 mb-2">
                              {job.prompt}
                            </p>
                            
                            <div className="flex items-center gap-2 text-[10px] text-zinc-500 mb-2">
                              <span>{job.duration}s</span>
                              <span>•</span>
                              <span>{job.resolution}</span>
                              <span>•</span>
                              <span>{job.aspect_ratio}</span>
                            </div>
                            
                            {job.status === "completed" && job.video_url && (
                              <video
                                src={getAssetUrl(job.video_url)}
                                controls
                                className="w-full rounded-lg bg-black mb-2"
                              />
                            )}
                            
                            <div className="flex gap-2">
                              {job.status === "completed" && job.video_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex-1 text-[10px] h-7 text-green-400 hover:bg-green-500/10"
                                  onClick={() => handleDownloadVideo(job.video_url, job.job_id)}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[10px] h-7 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteJob(job.job_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              )}
            </Card>

            {/* Tips Card */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Tips for Best Results
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-zinc-400">
                <ul className="list-disc list-inside space-y-1 text-zinc-500">
                  <li><strong className="text-amber-400">1-2 images</strong> work best for character consistency</li>
                  <li>Use <strong className="text-purple-400">@Image1</strong> syntax to reference images in prompt</li>
                  <li>More than 3 character images can confuse the model</li>
                  <li>6-7 total references produce most reliable results</li>
                  <li>Use consistent lighting and style across images</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
